import { vec2 } from 'gl-matrix';
import { PlaneView } from '../../../../view/views/PlaneView';
import { ViewportCursor } from './ViewportCursor';

// ───────── Sun icon geometry (all distances from centre) ─────────

/** Radius of the inner circle (centre dot). */
const CIRCLE_R = 2.5;

/** Distance from centre where rays begin. */
const RAY_INNER_R = 6;

/** Distance from centre where rays end. */
const RAY_OUTER_R = 13;

/** Stroke width of the ray lines (foreground).
 *  The black outline is drawn at RAY_WIDTH + 1 for contrast. */
const RAY_WIDTH = 1.5;

/** Gap in pixels between the end of the rays and the wedge arrow. */
const RAY_TO_WEDGE_GAP = 5;

/** Depth of the wedge chevron (tail to apex). */
const WEDGE_DEPTH = 1.5;

/** Half-height of the wedge chevron. */
const WEDGE_HALF = 2;

/** Padding beyond the outermost drawn element (accounts for
 *  stroke width so nothing gets clipped). */
const EDGE_PADDING = 2;

// ───────── Label constants ─────────

/** Font size for the image label (px). */
const LABEL_FONT_SIZE = 10;

/** Stroke width for the label outline (px). */
const LABEL_OUTLINE_WIDTH = 2.5;

// ───────── Derived layout constants ─────────

/** Furthest drawn point from centre (wedge apex). */
const MAX_RADIUS = RAY_OUTER_R + RAY_TO_WEDGE_GAP + WEDGE_DEPTH;

/**
 * Size of the cursor icon in pixels.
 * Derived from geometry so adjusting any constant above
 * (e.g. RAY_TO_WEDGE_GAP) automatically resizes the icon.
 */
const ICON_SIZE = Math.ceil((MAX_RADIUS + EDGE_PADDING) * 2);

/** Centre of the icon. */
const ICON_CENTER = ICON_SIZE / 2;

/** Options for configuring the windowing cursor appearance. */
export interface WindowingCursorOptions {
    /** Foreground color for the icon. Default: '#fff'. */
    foregroundColor?: string;
    /**
     * Image label shown at bottom-right (e.g. 'I', 'II').
     * Omit or set undefined to hide the label.
     */
    imageLabel?: string;
}

/**
 * Custom cursor overlay for global windowing (right-click-drag).
 *
 * Uses a fixed-position DOM overlay element instead of CSS
 * `cursor` because the browser hides all cursors during pointer
 * lock. The overlay stays visible at the drag start position
 * regardless of whether pointer lock is active or not.
 *
 * Displays a small sun/brightness icon with a directional arrow
 * pointing in one of 8 compass directions, reflecting the
 * cumulative WW/WC adjustment direction.
 *
 * All 8 SVG variants are pre-cached in the constructor — only
 * the overlay's innerHTML swap changes per frame.
 */
export class WindowingCursor extends ViewportCursor {
    /** Foreground color for the SVG icon elements. */
    private readonly foregroundColor: string;

    /** Optional image label (e.g. 'I', 'II'). */
    private readonly imageLabel: string | undefined;

    /** 8 pre-built SVG strings (one per compass direction). */
    private readonly cachedSvgs: string[];

    /** SVG with no directional arrow (shown before any movement). */
    private readonly neutralSvg: string;

    /**
     * Current direction index (0–7), or -1 for neutral.
     * Only updated when the quantized direction actually changes.
     */
    private currentIndex = -1;

    /** The fixed-position overlay element showing the icon. */
    private overlayElement: HTMLDivElement | null = null;

    constructor(views: PlaneView[] | null, options?: WindowingCursorOptions) {
        super(views);
        const { foregroundColor = '#fff', imageLabel } = options ?? {};
        this.foregroundColor = foregroundColor;
        this.imageLabel = imageLabel;

        // Pre-generate the neutral SVG (sun icon only, no arrow)
        this.neutralSvg = WindowingCursor.buildSunSvg(
            this.foregroundColor,
            this.imageLabel
        );

        // Pre-generate all 8 directional SVGs
        // Index 0 = E (0°), 1 = SE (45°), … 7 = NE (315°)
        this.cachedSvgs = [];
        for (let i = 0; i < 8; i++) {
            const angleDeg = i * 45;
            this.cachedSvgs.push(
                WindowingCursor.buildSunWithArrowSvg(
                    angleDeg,
                    this.foregroundColor,
                    this.imageLabel
                )
            );
        }
    }

    /**
     * Show the cursor overlay at the given screen position.
     * Also hides the system cursor on all canvas parent elements.
     * @param clientX Screen X coordinate (centre of icon).
     * @param clientY Screen Y coordinate (centre of icon).
     */
    public show(clientX: number, clientY: number): void {
        this.hideSystemCursor();

        const el = document.createElement('div');
        el.style.position = 'fixed';
        el.style.left = `${clientX - ICON_SIZE / 2}px`;
        el.style.top = `${clientY - ICON_SIZE / 2}px`;
        el.style.width = `${ICON_SIZE}px`;
        el.style.height = `${ICON_SIZE}px`;
        el.style.pointerEvents = 'none';
        el.style.zIndex = '99999';
        el.innerHTML = this.neutralSvg;
        document.body.appendChild(el);
        this.overlayElement = el;
    }

    /**
     * Recalculate the direction index from cumulative delta and
     * update the overlay SVG if the index changed.
     */
    public updateDirection(totalDelta: vec2): void {
        const dx = totalDelta[0];
        const dy = totalDelta[1];

        // Need some minimum movement to establish direction
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
            return;
        }

        const angleRad = Math.atan2(dy, dx);
        // Snap to nearest 45° → index 0–7
        const index = ((Math.round(angleRad / (Math.PI / 4)) % 8) + 8) % 8;

        if (index !== this.currentIndex) {
            this.currentIndex = index;
            if (this.overlayElement) {
                this.overlayElement.innerHTML = this.cachedSvgs[index];
            }
        }
    }

    /**
     * Restore the system cursor visibility without hiding the
     * overlay. Used as a graceful fallback when pointer lock is
     * denied — the cursor becomes visible again while windowing
     * continues to work.
     */
    public restoreCursorVisibility(): void {
        document.body.style.cursor = '';
        this.resetParentsCursor();
    }

    /**
     * Remove the overlay and restore the system cursor.
     */
    public hide(): void {
        if (this.overlayElement) {
            this.overlayElement.remove();
            this.overlayElement = null;
        }
        this.restoreSystemCursor();
    }

    // ───────── private helpers ─────────

    /**
     * Hide the system cursor on canvas parents AND document.body
     * so the cursor stays hidden even when the mouse moves outside
     * the viewport area (but still inside the browser window).
     */
    private hideSystemCursor(): void {
        this.setParentsCursorStyle('none');
        // Also hide on body so cursor stays hidden when
        // the pointer drifts outside the viewport area.
        document.body.style.cursor = 'none';
    }

    /** Restore the default system cursor on all canvas parents and body. */
    private restoreSystemCursor(): void {
        document.body.style.cursor = '';
        this.resetParentsCursor();
    }

    // ───────── SVG builders ─────────

    /**
     * SVG `<defs>` block with a drop-shadow filter that creates
     * a soft dark halo around cursor elements. Ensures visibility
     * on both bright and dark image backgrounds without a hard
     * contrasting outline.
     */
    private static readonly SHADOW_FILTER_DEFS = [
        '<defs><filter id="ds" x="-30%" y="-30%"',
        ' width="160%" height="160%">',
        '<feGaussianBlur in="SourceAlpha" stdDeviation="1.5"',
        ' result="blur"/>',
        '<feMerge>',
        '<feMergeNode in="blur"/>',
        '<feMergeNode in="SourceGraphic"/>',
        '</feMerge>',
        '</filter></defs>',
    ].join('');

    /**
     * Build SVG `<text>` elements for the image label.
     * Renders a black-stroked outline behind a foreground-filled
     * text, positioned at bottom-right of the icon.
     */
    private static buildLabelElements(label: string, fg: string): string[] {
        const x = ICON_CENTER + 18;
        const y = ICON_CENTER + 15;
        const common =
            `x="${x}" y="${y}"` +
            ` font-family="Arial, sans-serif"` +
            ` font-size="${LABEL_FONT_SIZE}"` +
            ` font-weight="bold"` +
            ` text-anchor="middle"`;
        return [
            // Black outline for contrast
            `<text ${common} fill="#000" stroke="#000"` +
                ` stroke-width="${LABEL_OUTLINE_WIDTH}"` +
                `>${label}</text>`,
            // Foreground fill
            `<text ${common} fill="${fg}"` + ` stroke="none">${label}</text>`,
        ];
    }

    /**
     * Build SVG string with only the sun/brightness icon
     * (no directional arrow). A drop-shadow filter provides
     * a soft dark halo for visibility on bright backgrounds.
     */
    private static buildSunSvg(fg: string, label?: string): string {
        return [
            '<svg xmlns="http://www.w3.org/2000/svg"',
            ` width="${ICON_SIZE}" height="${ICON_SIZE}"`,
            ` viewBox="0 0 ${ICON_SIZE} ${ICON_SIZE}"`,
            ` fill="none" stroke="${fg}" stroke-width="2"`,
            ' stroke-linecap="round" stroke-linejoin="round">',
            WindowingCursor.SHADOW_FILTER_DEFS,
            '<g filter="url(#ds)">',
            // Black outline circle drawn at a slightly larger
            // radius so its stroke does not paint over the
            // transparent centre.
            `<circle cx="${ICON_CENTER}" cy="${ICON_CENTER}"` +
                ` r="${CIRCLE_R + 0.5}"` +
                ' stroke="#000" stroke-width="2"/>',
            `<circle cx="${ICON_CENTER}" cy="${ICON_CENTER}"` +
                ` r="${CIRCLE_R}"/>`,
            // Rays (8 short lines from centre outward)
            ...WindowingCursor.buildRayLines('#000', RAY_WIDTH + 1),
            ...WindowingCursor.buildRayLines(fg, RAY_WIDTH),
            // Image label (e.g. 'I', 'II') at bottom-right
            ...(label ? WindowingCursor.buildLabelElements(label, fg) : []),
            '</g>',
            '</svg>',
        ].join('');
    }

    /**
     * Build SVG string with the sun icon and a directional
     * wedge rotated to {@link angleDeg} around the centre.
     * A drop-shadow filter provides a soft dark halo for
     * visibility on bright backgrounds.
     */
    private static buildSunWithArrowSvg(
        angleDeg: number,
        fg: string,
        label?: string
    ): string {
        // Wedge (chevron) orbiting outside the rays, separated
        // by RAY_TO_WEDGE_GAP pixels from the ray tips.
        const wedgeTail = ICON_CENTER + RAY_OUTER_R + RAY_TO_WEDGE_GAP;
        const wedgeApex = wedgeTail + WEDGE_DEPTH;
        const wedgeTop = ICON_CENTER - WEDGE_HALF;
        const wedgeBottom = ICON_CENTER + WEDGE_HALF;
        const wedgePoints =
            `${wedgeTail},${wedgeTop}` +
            ` ${wedgeApex},${ICON_CENTER}` +
            ` ${wedgeTail},${wedgeBottom}`;
        return [
            '<svg xmlns="http://www.w3.org/2000/svg"',
            ` width="${ICON_SIZE}" height="${ICON_SIZE}"`,
            ` viewBox="0 0 ${ICON_SIZE} ${ICON_SIZE}"`,
            ` fill="none" stroke="${fg}" stroke-width="2"`,
            ' stroke-linecap="round" stroke-linejoin="round">',
            WindowingCursor.SHADOW_FILTER_DEFS,
            '<g filter="url(#ds)">',
            // Sun icon (same as neutral)
            `<circle cx="${ICON_CENTER}" cy="${ICON_CENTER}"` +
                ` r="${CIRCLE_R + 0.5}"` +
                ' stroke="#000" stroke-width="2"/>',
            `<circle cx="${ICON_CENTER}" cy="${ICON_CENTER}"` +
                ` r="${CIRCLE_R}"/>`,
            ...WindowingCursor.buildRayLines('#000', RAY_WIDTH + 1),
            ...WindowingCursor.buildRayLines(fg, RAY_WIDTH),
            // Directional wedge — drawn pointing East (→), then
            // rotated to the desired angle
            `<g transform="rotate(${angleDeg}, ${ICON_CENTER}, ${ICON_CENTER})">`,
            // Wedge outline (black) for contrast
            `<polyline points="${wedgePoints}"` +
                ' stroke="#000" stroke-width="3" fill="none"/>',
            // Wedge foreground
            `<polyline points="${wedgePoints}" fill="none"/>`,
            '</g>',
            // Image label (e.g. 'I', 'II') at bottom-right
            ...(label ? WindowingCursor.buildLabelElements(label, fg) : []),
            '</g>',
            '</svg>',
        ].join('');
    }

    /** Generate 8 ray lines radiating from the sun centre. */
    private static buildRayLines(
        stroke: string,
        strokeWidth: number
    ): string[] {
        const cx = ICON_CENTER;
        const cy = ICON_CENTER;
        const innerR = RAY_INNER_R;
        const outerR = RAY_OUTER_R;
        const lines: string[] = [];
        for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI) / 4;
            const x1 = cx + innerR * Math.cos(angle);
            const y1 = cy + innerR * Math.sin(angle);
            const x2 = cx + outerR * Math.cos(angle);
            const y2 = cy + outerR * Math.sin(angle);
            lines.push(
                `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}"` +
                    ` x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}"` +
                    ` stroke="${stroke}" stroke-width="${strokeWidth}"/>`
            );
        }
        return lines;
    }
}
